import { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import { lprSearchServiceInstance as lprSearchService } from "../../services/LPRSearchService";

/**
 * Custom hook for managing LPR search state and API interactions.
 * Accepts a cancelToken from useCancellationToken to support request cancellation.
 */
const useLPRSearch = () => {
	const [results, setResults] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [hasSearched, setHasSearched] = useState(false);
	const [error, setError] = useState(null);
	const mountedRef = useRef(true);

	// Track mount status to prevent setState after unmount
	useEffect(() => {
		mountedRef.current = true;
		return () => { mountedRef.current = false; };
	}, []);

	const search = useCallback(async (params, cancelToken) => {
		setIsLoading(true);
		setError(null);

		try {
			const response = await lprSearchService.searchLPRReads(params, cancelToken);
			if (mountedRef.current) {
				setResults(response.data.collection || []);
				setTotalCount(response.data.totalCount || 0);
				setHasSearched(true);
			}
		} catch (err) {
			if (axios.isCancel(err)) return;
			if (mountedRef.current) {
				setError(err.message || "An error occurred while searching");
				setResults([]);
				setTotalCount(0);
			}
		} finally {
			if (mountedRef.current) {
				setIsLoading(false);
			}
		}
	}, []);

	const clearResults = useCallback(() => {
		setResults([]);
		setTotalCount(0);
		setHasSearched(false);
	}, []);

	return {
		results,
		totalCount,
		isLoading,
		hasSearched,
		error,
		search,
		clearResults,
	};
};

export default useLPRSearch;
